import React from "react";
import { ArrowRight, Sparkles, Zap, Leaf } from "lucide-react";
import { useNavigate } from "react-router-dom";

const HERO_BG = "/station_team.jpg";
const FALLBACK_BG = "/generated/hero.png";

export default function Hero() {
  const navigate = useNavigate();

  function goToSection(id) {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    // If not on home, navigate to root and scroll after a short delay
    try {
      navigate("/");
    } catch (e) {
      try {
        window.location.hash = "#/";
      } catch (err) {}
    }
    setTimeout(() => {
      const el2 = document.getElementById(id);
      if (el2) el2.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
  }
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          onError={(e) => {
            e.currentTarget.src = FALLBACK_BG;
          }}
          alt="INDIANECOFUEL Station Karnal — team and brand"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 hero-side-scrim" />
        <div className="absolute inset-0 hero-vertical-scrim" />
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 25% 50%, rgba(11,122,59,0.25), transparent 60%)",
          }}
        />
      </div>

      {/* Decorative blobs */}
      <div
        className="eco-blob bg-eco-light"
        style={{ width: 520, height: 520, top: "10%", left: "-10%" }}
      />
      <div
        className="eco-blob bg-saffron"
        style={{
          width: 380,
          height: 380,
          bottom: "-10%",
          right: "-5%",
          opacity: 0.25,
        }}
      />

      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(14)].map((_, i) => (
          <span
            key={i}
            className="float-particle absolute block rounded-full"
            style={{
              left: `${(i * 7 + 5) % 95}%`,
              top: `${(i * 13 + 8) % 90}%`,
              width: `${4 + (i % 4)}px`,
              height: `${4 + (i % 4)}px`,
              background: i % 3 === 0 ? "#FF9933" : "#138808",
              boxShadow:
                i % 3 === 0 ? "0 0 14px #FF9933" : "0 0 14px #138808",
              animationDelay: `${i * 0.5}s`,
              opacity: 0.6,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-8">
          <div className="reveal">
            <span className="overline">
              India · Clean Energy · 2026
            </span>
          </div>

          <h1 className="reveal font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold leading-[0.95] tracking-tight mt-6">
            Fueling India&apos;s <br />
            <span className="gradient-text-eco">Green Future.</span>
          </h1>

          <p className="reveal mt-8 text-lg md:text-xl text-white/70 max-w-2xl leading-relaxed">
            Advanced eco fuel solutions driving sustainable mobility and cleaner energy for tomorrow.
            Biofuels, EV infrastructure, and renewable technology engineered for the next century of Indian growth.
          </p>

          <div className="reveal mt-10 flex flex-wrap gap-4 items-center">
            <button onClick={() => goToSection("services")} className="btn-saffron">
              Explore Solutions <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => goToSection("opportunities")} className="btn-outline-green">
              <Leaf className="w-4 h-4 text-eco-light" /> Partner With Us
            </button>
          </div>

          {/* Mini stats */}
          <div className="reveal mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl">
            {[
              { icon: Sparkles, k: "100+", v: "Locations Planned" },
              { icon: Zap, k: "24/7", v: "Charging Uptime" },
              { icon: Leaf, k: "Net-Zero", v: "By 2050 Goal" },
            ].map((s, i) => (
              <div key={i} className="md:border-l md:border-eco-light/30 md:pl-4 pl-0">
                <s.icon className="w-4 h-4 text-eco-light mb-2" />
                <div className="font-display text-xl md:text-2xl font-semibold">{s.k}</div>
                <div className="text-xs text-white/50 mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE REMOVED COMPLETELY */}
      </div>
    </section>
  );
}
